import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { filter, map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { API_PATHS } from '../common/constants/common.constants';

@Injectable({
  providedIn: 'root'
})
export class SocketService implements OnDestroy {
  private socket: WebSocket | undefined;
  private readonly URL: string = environment.WS_BASE_URL;
  private messageSubject: Subject<any> = new Subject<any>();
  private eventSource?: EventSource;
  private priceSubject = new BehaviorSubject<any>({});
  public prices$: Observable<any> = this.priceSubject.asObservable();
  constructor(private auth: AuthService) {
    this.init();
  }

  init(): void {
    const token = this.auth.getSessionToken();
    if (!token) {
      console.warn("No session token found, socket connection might fail or restricted.");
    }

    const connectionUrl = `${this.URL}?token=${token || ''}`;

    try {
      this.socket = new WebSocket(connectionUrl);

      this.socket.onopen = () => {
        // console.log("WebSocket Connected Successfully");
      };

      this.socket.onmessage = (event) => {
        // console.log('RAW SOCKET MESSAGE:', event.data);

        try {
          const data = JSON.parse(event.data);
          // console.log('PARSED SOCKET MESSAGE:', data);
          this.messageSubject.next(data);
        } catch (e) {
          console.error('Parse error', event.data);
          this.messageSubject.next(event.data);
        }
      };


      this.socket.onerror = (error) => {
        console.error("WebSocket Error:", error);
      };

      this.socket.onclose = (event) => {
        console.log("WebSocket Disconnected", event.reason);
        // Optional: Reconnection logic
        if (!event.wasClean) {
          setTimeout(() => this.init(), 5000);
        }
      };
    } catch (e) {
      console.error("Failed to initialize WebSocket:", e);
    }
  }

  /**
   * Method to send data to the server
   * @param eventName The name of the event (e.g., 'NEW_COMMENT')
   * @param data The data payload to send
   */
  emit(eventName: string, data: any): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn("Socket not connected. Attempting to reconnect...");
      this.init();
      // Optionally queue messages or retry after a delay
      return;
    }

    const payload = JSON.stringify({
      event: eventName,
      data: data
    });
    this.socket.send(payload);
  }

  /**
   * Method to listen for events from the server and return an Observable
   * @param eventName The name of the event to listen for (e.g., 'NEW_COMMENT')
   * @returns An Observable stream of the received data
   */
  listen<T>(eventName: string): Observable<T> {
    return this.messageSubject.asObservable().pipe(
      filter(msg => msg?.event === eventName),
      map(msg => msg as T)
    );
  }


  // Helper method to disconnect
  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
    }
  }
  /**
     * Sends the payload exactly as it is, without wrapping it in a 'data' property.
     * Use this for events like LIKE_ACTION.
     */
  emitRaw(payload: any): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('Socket not connected');
      return;
    }

    this.socket.send(JSON.stringify(payload));
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /** Start streaming market prices */
  streamMarketPrices(marketId: string) {
    if (this.eventSource) {
      this.eventSource.close();
    }

    let baseUrl = environment.TRADING_ENGINE_URL;
    const path = API_PATHS.GET_MARKET_PRICES_STREAM.replace('{market_id}', marketId);

    // Ensure baseUrl doesn't end with slash if path starts with one, or vice-versa
    if (baseUrl.endsWith('/') && path.startsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    } else if (!baseUrl.endsWith('/') && !path.startsWith('/')) {
      baseUrl = baseUrl + '/';
    }

    this.eventSource = new EventSource(baseUrl + path);

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && data.prices) {
          this.priceSubject.next(data.prices);
        }
      } catch (err) {
        console.error('Error parsing SSE data', err);
      }
    };

    this.eventSource.onerror = (err) => {
      console.error('SSE connection error', err);
      // Optional: auto-reconnect logic
    };
  }

  stopMarketPrices() {
    this.eventSource?.close();
  }



}
